import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Card, Input, Select, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import DataTable from '../../components/DataTable';
import FilterPanel from '../../components/FilterPanel';
import Pagination from '../../components/Pagination';
import DateRangePicker, { type DateRange } from '../../components/DateRangePicker';
import RiskBadge from '../../components/RiskBadge';
import ScoreBadge from '../../components/ScoreBadge';
import AiAnalysisBadge from '../../components/AiAnalysisBadge';
import { callsApi } from '../../api/calls.api';
import { lookupsApi } from '../../api/lookups.api';
import { toApiError } from '../../api/client';
import type { CallListItem, CallListQuery } from '../../types/calls';

const STATUS_LABELS: Record<string, string> = {
  Received: 'دریافت‌شده',
  Processing: 'در حال پردازش',
  Processed: 'پردازش‌شده',
  Error: 'خطا',
  RequiresReprocessing: 'نیازمند پردازش مجدد',
};

const fmtDuration = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export default function CallList() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [query, setQuery] = useState<CallListQuery>({ page: 1, pageSize: 50 });

  const callsQuery = useQuery({ queryKey: ['calls', query], queryFn: () => callsApi.list(query) });
  const operators = useQuery({ queryKey: ['lookup-operators'], queryFn: lookupsApi.operators });
  const teams = useQuery({ queryKey: ['lookup-teams'], queryFn: lookupsApi.teams });
  const units = useQuery({ queryKey: ['lookup-units'], queryFn: lookupsApi.units });

  const patch = (p: Partial<CallListQuery>) => setQuery((q) => ({ ...q, page: 1, ...p }));

  const onDate = (range: DateRange) =>
    patch({ from: range?.[0]?.toISOString(), to: range?.[1]?.toISOString() });

  const onExport = async () => {
    try {
      await callsApi.exportCsv(query);
    } catch (err) {
      message.error(toApiError(err).message);
    }
  };

  const columns: ColumnsType<CallListItem> = [
    { title: 'زمان', dataIndex: 'timestamp', key: 'timestamp', width: 160, render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
    { title: 'شناسه تماس', dataIndex: 'externalCallId', key: 'externalCallId' },
    { title: 'اپراتور', dataIndex: 'operatorName', key: 'operatorName' },
    { title: 'مدت', dataIndex: 'durationSeconds', key: 'durationSeconds', width: 90, render: fmtDuration },
    {
      title: 'وضعیت',
      dataIndex: 'processingStatus',
      key: 'processingStatus',
      render: (s: string) => <Tag color={s === 'Processed' ? 'green' : s === 'Error' ? 'red' : 'blue'}>{STATUS_LABELS[s] ?? s}</Tag>,
    },
    {
      title: <Space size={4}>ریسک <AiAnalysisBadge compact /></Space>,
      key: 'riskLevel',
      render: (_, r) => <RiskBadge level={r.riskLevel} />,
    },
    {
      title: <Space size={4}>امتیاز کیفیت <AiAnalysisBadge compact /></Space>,
      key: 'overallQualityScore',
      render: (_, r) => <ScoreBadge score={r.overallQualityScore} />,
    },
    {
      title: 'بازبینی',
      key: 'review',
      render: (_, r) =>
        r.humanReviewRequired
          ? <Tag color={r.humanReviewStatus === 'Reviewed' ? 'green' : 'orange'}>{r.humanReviewStatus === 'Reviewed' ? 'بازبینی‌شده' : 'نیازمند بازبینی'}</Tag>
          : <span>—</span>,
    },
    { title: 'جزئی', key: 'partialScore', width: 70, render: (_, r) => (r.partialScore ? <Tag>جزئی</Tag> : null) },
  ];

  return (
    <Card
      title={<Typography.Title level={4} style={{ margin: 0 }}>تماس‌ها</Typography.Title>}
      extra={<Button onClick={onExport}>خروجی CSV</Button>}
    >
      <FilterPanel onReset={() => setQuery({ page: 1, pageSize: 50 })}>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>بازه زمانی</Typography.Text>
          <DateRangePicker onChange={onDate} />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>ریسک</Typography.Text>
          <Select
            allowClear placeholder="همه" style={{ width: 140 }} value={query.risk}
            onChange={(v) => patch({ risk: v })}
            options={[{ value: 'Low', label: 'کم' }, { value: 'Medium', label: 'متوسط' }, { value: 'High', label: 'زیاد' }]}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>وضعیت</Typography.Text>
          <Select
            allowClear placeholder="همه" style={{ width: 170 }} value={query.status}
            onChange={(v) => patch({ status: v })}
            options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>جست‌وجو</Typography.Text>
          <Input.Search
            allowClear placeholder="شناسه/متن رونوشت/خلاصه" style={{ width: 240 }}
            onSearch={(v) => patch({ search: v || undefined })}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>اپراتور</Typography.Text>
          <Select
            allowClear showSearch optionFilterProp="label" placeholder="همه" style={{ width: 180 }}
            value={query.operatorId} loading={operators.isLoading}
            onChange={(v) => patch({ operatorId: v })}
            options={(operators.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>تیم</Typography.Text>
          <Select
            allowClear placeholder="همه" style={{ width: 160 }}
            value={query.teamId} loading={teams.isLoading}
            onChange={(v) => patch({ teamId: v })}
            options={(teams.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>واحد</Typography.Text>
          <Select
            allowClear placeholder="همه" style={{ width: 160 }}
            value={query.unitId} loading={units.isLoading}
            onChange={(v) => patch({ unitId: v })}
            options={(units.data ?? []).map((u) => ({ value: u.id, label: u.name }))}
          />
        </div>
      </FilterPanel>

      <DataTable<CallListItem>
        columns={columns}
        dataSource={callsQuery.data?.items ?? []}
        loading={callsQuery.isLoading}
        onRow={(r) => ({ onClick: () => navigate(`/calls/${r.id}`), style: { cursor: 'pointer' } })}
      />

      <Pagination
        total={callsQuery.data?.total ?? 0}
        page={query.page ?? 1}
        pageSize={query.pageSize ?? 50}
        onChange={(page, pageSize) => setQuery((q) => ({ ...q, page, pageSize }))}
      />
    </Card>
  );
}
